import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  DocumentIcon,
  GlobeAltIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal, ModalBody, ModalContent } from '@heroui/react';
import React, { FC, useState } from 'react';
import { useTheme } from 'styled-components';

import { useIsMobile } from '../../hooks/useIsMobile';
import { getBytescaleImageUrl } from '../../utils/getImageUrl';
import { Flex } from '../Flex';
import { FileCardWrapper, FilenameBox, FilenameEllipsis, ImageThumbnail, PreviewPopover } from './FileCard.styled';

type FileTypeProps = 'IMAGE' | 'PDF' | 'URL';
type FlagReturn = { type: 'iso' | 'local'; value: string };

interface FileCardProps {
    onRemove?: (fileName?: string) => void;
    onPreview?: () => any | undefined;
    fileName: string;
    fileType: FileTypeProps;
    currentFlag?: FlagReturn;
    showDragHandle?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
}

export const FileCard: FC<FileCardProps> = ({
  onRemove,
  onPreview,
  fileType,
  fileName,
  currentFlag,
  showDragHandle = false,
  onDragStart,
}) => {
  const [hover, setHover] = useState(false);
  const [cardHover, setCardHover] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const isMobile = useIsMobile();

  const getLastPathSegment = (url: string): string => {
    if (!url) return '';
    const clean = url.replace(/[#?].*$/, '').replace(/\/+$/, '');
    const last = clean.split('/').pop() || '';
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  };

  const shortenFileName = (name: string, maxLength: number = 20): string => {
    if (name.length <= maxLength) return name;
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
    const baseName = name.slice(0, name.length - ext.length);
    const charsToShow = maxLength - ext.length - 3;
    if (charsToShow <= 0) return `...${ext}`;
    return `${baseName.slice(0, charsToShow)}...${ext}`;
  };

  const displayFileName = (): string => {
    const fullName = getLastPathSegment(fileName) || 'No data file name...';
    return isMobile ? shortenFileName(fullName, 15) : fullName;
  };

  const getFileIcon = () => {
    const iconProps = { width: 20, height: 20 };

    switch (fileType) {
      case 'PDF':
        return <DocumentIcon {...iconProps} style={{ color: '#9333ea' }} />;
      case 'IMAGE':
        return <PhotoIcon {...iconProps} style={{ color: '#1a4d4d' }} />;
      case 'URL':
        return <GlobeAltIcon {...iconProps} style={{ color: '#10b981' }} />;
      default:
        return null;
    }
  };

  const shouldShowPreview = (fileType === 'IMAGE' || fileType === 'PDF') && hover;

  // Czysty, neutralny chip (spójny z nową drop-zone) — bez saturowanych teł per typ.
  // Sygnałem typu pliku pozostaje kolorowa ikona (getFileIcon), nie tło.
  const getColorScheme = () => {
    if (isDark) {
      return {
        background: '#242a33',
        border: '#374151',
        hoverBg: '#2c333d',
        hoverBorder: '#4b5563',
      };
    }
    return {
      background: '#ffffff',
      border: '#e5e7eb',
      hoverBg: '#f9fafb',
      hoverBorder: '#d1d5db',
    };
  };

  const colorScheme = getColorScheme();

  return (
    <>
    <FileCardWrapper
      data-name={currentFlag}
      style={{
        background: cardHover ? colorScheme.hoverBg : colorScheme.background,
        border: `1px solid ${cardHover ? colorScheme.hoverBorder : colorScheme.border}`,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={() => setCardHover(true)}
      onMouseLeave={() => setCardHover(false)}
    >
      <Flex direction="row" itemAlign="center" gap="8px" flex="1 1 auto" flexWrap="nowrap">
        <div style={{ flexShrink: 0 }}>
          {fileType === 'IMAGE' ? (
            <ImageThumbnail
              $imageUrl={getBytescaleImageUrl(fileName, 'BYTESCALE', { query: 'w=96&h=96&fit=crop&f=webp' })}
            />
          ) : (
            getFileIcon()
          )}
        </div>

        <FilenameBox
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <FilenameEllipsis>
            {displayFileName()}
          </FilenameEllipsis>

          {shouldShowPreview && (
            <PreviewPopover>
              {fileType === 'IMAGE' && (
              <img
                src={fileName}
                alt="Podgląd"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: 6,
                }}
              />
              )}
              {fileType === 'PDF' && (
              <iframe
                src={`${fileName}#view=FitH`}
                title="PDF Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 6,
                }}
              />
              )}
            </PreviewPopover>
          )}
        </FilenameBox>
      </Flex>

      <Flex direction="row" gap="4px" itemAlign="center" justifyContent="flex-end" flex="0 0 auto" flexWrap="nowrap">
        {/* Preview Button */}
        {onPreview && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // Dla obrazu pokazujemy modal z pełnym zdjęciem (window.open bywa blokowany).
            if (fileType === 'IMAGE') {
              setPreviewOpen(true);
            } else {
              onPreview();
            }
          }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1a4d4d',
            transition: 'background 0.2s',
          }}
        >
          <ArrowTopRightOnSquareIcon width={16} height={16} />
        </button>
        )}

        {/* Remove Button */}
        {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(fileName);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ef4444',
            transition: 'background 0.2s',
          }}
        >
          <TrashIcon width={16} height={16} />
        </button>
        )}

        {/* Drag Handle */}
        {showDragHandle && (
        <div
          draggable
          onDragStart={onDragStart}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'grab',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDark ? '#9ca3af' : '#6b7280',
            transition: 'background 0.2s',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Bars3Icon width={16} height={16} />
        </div>
        )}
      </Flex>
    </FileCardWrapper>

    {/* Modal z pełnym zdjęciem — otwierany przyciskiem podglądu (tylko dla obrazów). */}
    {fileType === 'IMAGE' && (
      <Modal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        size="3xl"
        backdrop="blur"
        scrollBehavior="inside"
        classNames={{
          // Widoczny „X" na tle zdjęcia — ciemne kółko z białą ikoną.
          closeButton: 'z-50 top-3 right-3 bg-black/55 text-white '
            + 'hover:bg-black/75 active:bg-black/80',
        }}
      >
        <ModalContent>
          <ModalBody className="p-2 flex items-center justify-center">
            <img
              src={fileName}
              alt={displayFileName()}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    )}
    </>
  );
};
